import { DemoBadge } from "app/pages/sales-intelligence/common-components/badge/DemoBadge";
import { NewBadge } from "app/pages/sales-intelligence/common-components/badge/NewBadge";
import { showsIndustryTaxonomy } from "app/pages/sales-intelligence/modules/industry/utils/industryVisibility";

import IndustryTaxonomyFilter from "../../components/filters/IndustryTaxonomyFilter/IndustryTaxonomyFilter";
import IndustryTaxonomyFilterResult from "../../components/filters/IndustryTaxonomyFilter/IndustryTaxonomyFilterResult";
import { leadsFilterApiCategory } from "../../types/leadsFilterApiCategory";
import type { LeadsFilterBuildContext } from "../../types/leadsFilterBuildContext";
import { leadsFilterCategoryMap } from "../../types/leadsFilterCategory";
import leadsSearchFiltersI18nKeys from "../../utils/i18n";
import { defineIndustryTaxonomyFilter } from "../defineSourcedFilter";

import IndustryTaxonomyFilterModel from "./IndustryTaxonomyFilterModel";

/**
 * Chrome shared by the live card and its loading placeholder, so it cannot drift between them.
 *
 * Split by *how* the taxonomy was reached, not by what it does. An opted-in account owns the filter
 * permanently, so it is ordinary panel furniture: a NEW badge and default visibility, which makes it
 * pinnable like the legacy card it replaces. A demo borrows it for one session, so it is badged DEMO and
 * pinned always-visible — the demo exists to show this card, and a pinnable card could be pinned away.
 */
const chromeFor = (ctx: LeadsFilterBuildContext) =>
    ctx.isIndustryTaxonomyOptedIn
        ? ({
              tag: NewBadge,
              tooltipText: { key: leadsSearchFiltersI18nKeys.industryTaxonomyTooltip() },
          } as const)
        : ({
              tag: DemoBadge,
              tooltipText: { key: leadsSearchFiltersI18nKeys.industryTaxonomyTooltip() },
              visibility: "always-visible",
          } as const);

/**
 * The v2 taxonomy filter, reachable two ways (ADR-0013): permanently for an account an admin opted in, or
 * for one session through the industry demo. `showsIndustryTaxonomy` is the single condition — it also gates
 * the taxonomy query's `enabled` in `useLeadsFilterBuildContext`, so what is fetched and what is shown cannot
 * disagree.
 *
 * The veto in `render` is load-bearing, not a readiness check: disabling the taxonomy query does not clear
 * its cache, so the index survives a demo ending — this is the only thing that hides the card again.
 *
 * No `usages` restriction: unlike the demo (which can only be entered from a search surface, so it stays
 * search-only on its own), the opt-in applies everywhere the industry classification does — search, team
 * territories and scoring rules.
 */
export const industryTaxonomyDefinition = defineIndustryTaxonomyFilter({
    key: "industryTaxonomy",
    apiCategory: leadsFilterApiCategory.industryTaxonomy,
    category: leadsFilterCategoryMap.company,
    trackingName: "Industry Taxonomy",
    titleKey: { key: leadsSearchFiltersI18nKeys.industryTaxonomyName() },

    render: (ctx) =>
        showsIndustryTaxonomy(ctx)
            ? {
                  ...chromeFor(ctx),
                  filter: new IndustryTaxonomyFilterModel({
                      options: ctx.industryTaxonomy,
                  }),
                  filterView: IndustryTaxonomyFilter,
                  filterResultView: IndustryTaxonomyFilterResult,
              }
            : undefined,

    // Holds the industry slot while the taxonomy index itself is fetching. Mirrors the veto in `render`:
    // without it the placeholder would show for a filter that never appears. The legacy card needs no
    // counterpart — the opt-in that chooses between the two is settled before the panel renders, so only one
    // of them is ever in play.
    buildPending: (ctx) =>
        showsIndustryTaxonomy(ctx)
            ? {
                  ...chromeFor(ctx),
                  bodyText: { key: leadsSearchFiltersI18nKeys.industryTaxonomyLoading() },
              }
            : undefined,
});
